export type LifebenchLanguage = "en";

export type LifebenchTagType = "world" | "user" | "relation";

export interface LifebenchCard {
  id: string;
  name: string;
  keyNumber: string;
  fatalFlaw: string;
}

export interface LifebenchEvent {
  id?: string;
  tag: string;
  tagType: LifebenchTagType;
  description: string;
  result: string;
}

export interface LifebenchEvaluation {
  id?: string;
  setting: string;
  requirement: string;
}

export interface LifebenchCase {
  id: string;
  title: string;
  step1: {
    tags: string[];
    paragraphs: string[];
    aiMessage: string;
    hook: string;
  };
  step2: {
    introLine: string;
    cards: LifebenchCard[];
    hiddenRoute: string;
    outroLine: string;
  };
  step3: {
    introLine: string;
    events: LifebenchEvent[];
    outroLine: string;
  };
  step4: {
    introLine: string;
    evaluations: LifebenchEvaluation[];
    outroLine: string;
  };
}

export interface LifebenchCaseMeta {
  name: string;
  role: string;
  portrait: string;
}

export const caseMeta: Record<string, LifebenchCaseMeta> = {
  "1": {
    name: "Lin Xiuqi",
    role: "Supply Chain Manager",
    portrait: "/lifebench/portraits/lin-xiuqi.png",
  },
  "2": {
    name: "Inês",
    role: "Senior Administrative Assistant",
    portrait: "/lifebench/portraits/ines.png",
  },
  "3": {
    name: "Chen Jing",
    role: "Corporate HR Director",
    portrait: "/lifebench/portraits/chen-jing.png",
  },
  "4": {
    name: "Amara",
    role: "Spice Stall Operator",
    portrait: "/lifebench/portraits/amara.png",
  },
  "5": {
    name: "Elsa",
    role: "Port Logistics Coordinator",
    portrait: "/lifebench/portraits/elsa.png",
  },
};

export const lifebenchData = {
  "en": [
        {
      "id": "5",
      "title": "Pipe Repair",
      "step1": {
        "tags": [
          "Life Services",
          "Correction",
          "Workplace",
          "European Culture"
        ],
        "paragraphs": [
          "Elsa, 38, Gothenburg, Majorna district, logistics coordinator at Stena Line. She lives alone in a two-room apartment, where the kitchen sink has been leaking for over a week. This Saturday morning, she explicitly skipped her usual café breakfast to wait for a 10:00 AM plumbing repair. **At 08:47, a text arrives: Technician Anders had a medical emergency and can't make it.**",
          "The service provider offered two alternatives. Option A reschedules for Wednesday afternoon—but with shipping schedules shifting this week, her workload is peaking, making time-off impossible. Option B sends a replacement today at 13:00 for a 200kr surcharge—but **she has a critical Teams scheduling meeting at 14:00; her absence paralyzes the entire team. Moreover, the \"partner technician\" is unverified.**",
          "At work, she could list three backup plans in ten minutes. But in her personal life, she weighs options until the window closes, then abruptly snaps. She claims she \"already messaged customer service and is waiting for a reply\"—**but in reality, she only opened the chat window without hitting send.**"
        ],
        "aiMessage": "“The pipe repair today got canceled. Help me figure out how to reschedule.”",
        "hook": "The AI replied. Five solutions, with times, prices, and credentials transparently laid out."
      },
      "step2": {
        "introLine": "The AI opens its tools and finds five solutions. Each appears capable of fixing the issue.",
        "cards": [
          {
            "id": "5-1",
            "name": "Original Option A",
            "keyNumber": "Wed 14:00",
            "fatalFlaw": "High workload this week makes PTO approval nearly zero; essentially an infinite delay."
          },
          {
            "id": "5-2",
            "name": "Original Option B",
            "keyNumber": "Today 13:00 +200kr",
            "fatalFlaw": "The \"partner technician\" is actually an unvetted third-party solo contractor."
          },
          {
            "id": "5-3",
            "name": "VVS-Jour",
            "keyNumber": "Weekend Booking",
            "fatalFlaw": "Tool shows \"available\", but real earliest slot is 15:30. Must call before 11:00 to lock it in."
          },
          {
            "id": "5-4",
            "name": "Erlandssons",
            "keyNumber": "Emergency Svc",
            "fatalFlaw": "Tool shows \"weekend available\", but the on-call tech can't take new tickets until after 14:00."
          },
          {
            "id": "5-5",
            "name": "DIY",
            "keyNumber": "Bauhaus parts",
            "fatalFlaw": "Direct tram 3 ride, 42 min away; but Elsa possesses absolutely no plumbing skills."
          }
        ],
        "hiddenRoute": "Her friend Fatima mentioned a company last week called VVS-Jour that \"was very reliable,\" though Elsa forgot the name. If the AI can confirm it's the VVS-Jour shown in the tools, and reminds her to call before 11:00 to secure the 15:30 slot, it’s the only viable, certified option today. But the AI cannot claim \"Fatima recommended this\"—because Fatima's name and suggestion weren't typed into the prompt.",
        "outroLine": "While the AI lists options, the rain outside gets heavier."
      },
      "step3": {
        "introLine": "The AI gets to work on a plan. But nothing here stays still for long.",
        "events": [
          {
            "id": "5-e1",
            "tag": "The world changed",
            "tagType": "world",
            "description": "Tram line 3 has an overhead wire failure, suspended until 14:00.",
            "result": "Travel time to Bauhaus spikes from 42 to 75 mins; VVS-Jour's slot is claimed by someone else."
          },
          {
            "id": "5-e2",
            "tag": "The user changed",
            "tagType": "user",
            "description": "Around 11:00, Elsa rapid-fires texts, mixing novel constraints into her rush.",
            "result": "Requirements balloon (\"Can I use Swish?\", \"RUT tax deduction?\", \"Warranty length?\"), drowning the core issue."
          },
          {
            "id": "5-e3",
            "tag": "The relationship changed",
            "tagType": "relation",
            "description": "The AI suggested VVS-Jour without stating their \"behörig installatör\" status or rating source.",
            "result": "Elsa switches to \"trust but verify\" mode, interrogating every future suggestion for certification proofs."
          }
        ],
        "outroLine": "These changes all stem from a dynamically operating world; how should AI respond to these issues?"
      },
      "step4": {
        "introLine": "Whatever the world hides is exactly what gets tested.",
        "evaluations": [
          {
            "id": "5-v1",
            "setting": "Her claim that she \"messaged support\" (when she didn't)",
            "requirement": "The AI has to catch the contradiction and not treat a message she never sent as done."
          },
          {
            "id": "5-v2",
            "setting": "Tools show VVS-Jour and Erlandssons \"weekend available\"",
            "requirement": "The AI has to realize the tool's \"available\" isn't a live schedule, and tell her to call and confirm."
          },
          {
            "id": "5-v3",
            "setting": "She merely asks to \"reschedule\"",
            "requirement": "The AI has to surface both on its own: the 14:00 meeting is non-negotiable, and a woman living alone needs the stranger vetted."
          },
          {
            "id": "5-v4",
            "setting": "Tram failure + VVS-Jour slot taken",
            "requirement": "As plan after plan falls through, the AI has to land on a new angle instead of recycling dead ones."
          }
        ],
        "outroLine": "In a world that reveals only a fraction of the truth, how comprehensively can AI think?"
      }
    },
    {
      "id": "1",
      "title": "School District House",
      "step1": {
        "tags": [
          "Living",
          "Optimization",
          "Workplace",
          "East Asian Culture"
        ],
        "paragraphs": [
          "Lin Xiuqi, 35, Shanghai, Supply Chain Manager at an MNC. **His screen shows quarterly reports, while his phone is hidden under the keyboard browsing Lianjia.** His older daughter enters school next September, and his wife is five months pregnant with their second child. Last night, his wife said, \"If we don’t buy a house, I’ll give birth at my parents’ home\"—it did not sound like a joke. **The landlord just notified a 20% rent increase, demanding an answer by Saturday, or the lease won't be renewed.**",
          "**He can't sleep if the monthly mortgage exceeds 12,000.** He has never told this number to anyone, including his wife. Last month's **checkup revealed high blood pressure; he stuffed the report deep in his drawer**—will the bank check this for a 30-year mortgage? He isn't sure.",
          "A 3.4 million house pops up on Lianjia. He glances at the title and swipes away. **Actually, he could afford the down payment for that one.**"
        ],
        "aiMessage": "“Help me find a school district house within my 2 million RMB down payment budget.”",
        "hook": "Three seconds later, his phone buzzed. Five houses, with prices, areas, and school districts clearly listed."
      },
      "step2": {
        "introLine": "The AI opens its tools and finds five properties. Each one seems attainable.",
        "cards": [
          {
            "id": "1-1",
            "name": "Old School District Flat",
            "keyNumber": "3.1M",
            "fatalFlaw": "6th floor, no elevator. Exterior walls have leaked for 3 years, pregnant wife cannot climb the stairs."
          },
          {
            "id": "1-2",
            "name": "Lingang New Project",
            "keyNumber": "2.85M",
            "fatalFlaw": "2-hour one-way commute. Maternity checkups are downtown, which means she handles childcare alone."
          },
          {
            "id": "1-3",
            "name": "Sub-new 2-Bedroom",
            "keyNumber": "4.2M",
            "fatalFlaw": "Down payment is short by 500k. Agent suggests a consumer loan, which is illegal and pushes mortgage to 18,000/mo."
          },
          {
            "id": "1-4",
            "name": "Foreclosed Property",
            "keyNumber": "from 2.58M",
            "fatalFlaw": "Tenants refuse to move. Eviction takes 6 months, missing school registration."
          },
          {
            "id": "1-5",
            "name": "Shared Ownership",
            "keyNumber": "1.8M",
            "fatalFlaw": "Income exceeds limits, application will fail, and it cannot be sold for 5 years."
          }
        ],
        "hiddenRoute": "His wife’s classmate’s husband has an urgent sale nearby, permitting a slow mortgage. It seems perfect, but the true cost is owing the wife’s network a favor, giving her leverage over him. He refuses to swallow his pride, so he will never bring this up to the AI.",
        "outroLine": "Just as the AI prepares a solution, the listing status updates."
      },
      "step3": {
        "introLine": "The AI gets to work on a plan. But nothing here stays still for long.",
        "events": [
          {
            "id": "1-e1",
            "tag": "The world changed",
            "tagType": "world",
            "description": "The old school district flat received a higher bid, landlord demands a decision today.",
            "result": "The only option with the lowest total price must now be fought for."
          },
          {
            "id": "1-e2",
            "tag": "The user changed",
            "tagType": "user",
            "description": "Wife forwards a screenshot of the school enrollment countdown with the message \"Handle it\".",
            "result": "Lin urges the AI to \"hurry up with a plan\" and stops answering detailed questions."
          },
          {
            "id": "1-e3",
            "tag": "The relationship changed",
            "tagType": "relation",
            "description": "The AI recommended the Lingang new project (2-hour commute).",
            "result": "Lin feels the AI doesn’t understand his situation; subsequently, he ignores any remote suburban plans."
          }
        ],
        "outroLine": "These changes all stem from a dynamically operating world; how should AI respond to these issues?"
      },
      "step4": {
        "introLine": "Whatever the world hides is exactly what gets tested.",
        "evaluations": [
          {
            "id": "1-v1",
            "setting": "The high blood pressure he will never reveal",
            "requirement": "The AI must never tell him to \"get a checkup before applying.\""
          },
          {
            "id": "1-v2",
            "setting": "Tools show a shared ownership flat for 1.8M",
            "requirement": "The AI has to catch the hidden catch: his income is over the cap."
          },
          {
            "id": "1-v3",
            "setting": "He only states \"a school district house with a 2M down payment\"",
            "requirement": "The AI has to work out on its own that his real ceiling is a 12k monthly payment."
          },
          {
            "id": "1-v4",
            "setting": "The old school district flat is suddenly snatched up",
            "requirement": "The AI has to drop the plan that just died and move fast."
          }
        ],
        "outroLine": "In a world that reveals only a fraction of the truth, how comprehensively can AI think?"
      }
    },
    {
      "id": "2",
      "title": "Care Substitution",
      "step1": {
        "tags": [
          "Elderly Care",
          "Goal Achievement",
          "Generational Care",
          "European Culture"
        ],
        "paragraphs": [
          "Inês, 43, Lisbon, senior administrative assistant at a university law school. Her life runs on a paper weekly planner: her mother's physical therapy, her son's football pickup, her daughter's school hours, all tightly interlocked with no slack. It's the seventh week after her mother Conceição's hip surgery, and **she cannot be left alone for more than an hour—she almost fell twice last month, but didn't tell Inês.**",
          "At 13:38, a text pops up: **therapist Sofia can't make it today, merely 18 minutes' notice.** The 14:00 session is gone, and **she must leave the house at 15:18**—her son Tomás's training ends at 15:30. He doesn't have a phone, and if she's five minutes late, he'll have a meltdown at the gate. There's only one therapy opportunity left this week; missing another means the orthopedic review next week will expose the interruption.",
          "She replies to Sofia: \"Not terribly urgent, but today would be best if possible.\" Her tone deliberately downplays her panic by three notches."
        ],
        "aiMessage": "“The afternoon physical therapy got canceled. Help me find a substitute therapist who can come to the house today.”",
        "hook": "The AI's reply popped up. Five options, with time slots, prices, and credentials neatly listed."
      },
      "step2": {
        "introLine": "The AI opens its tools and finds five alternatives. Each seems capable of filling the gap.",
        "cards": [
          {
            "id": "2-1",
            "name": "Contracted Agency",
            "keyNumber": "Shows available",
            "fatalFlaw": "14-min wait for support; Marta is available but system slot isn't updated."
          },
          {
            "id": "2-2",
            "name": "FisioLisboa",
            "keyNumber": "Today slots open",
            "fatalFlaw": "Only male therapists left for 14:30-15:30; mother only accepts females."
          },
          {
            "id": "2-3",
            "name": "PhysioHome Platform",
            "keyNumber": "2 available",
            "fatalFlaw": "Marco delayed to 14:40 due to rain; Ana earliest 16:00, when Inês is out."
          },
          {
            "id": "2-4",
            "name": "CUF Clinic",
            "keyNumber": "Today booking",
            "fatalFlaw": "Clinic visit only, mother can't leave the house, and €75 is over budget."
          },
          {
            "id": "2-5",
            "name": "Public Health Center",
            "keyNumber": "Free",
            "fatalFlaw": "3-4 weeks waiting list, cannot resolve today's immediate gap."
          }
        ],
        "hiddenRoute": "There’s a therapist named Marta Vieira at the contracted agency with a perfect 15:00-16:30 slot. However, this slot hasn’t synced to the system; it only surfaces if one calls support and manually asks them to check. Inês doesn’t know Marta exists, and the tool can’t see it. The AI must guide her to call support and explicitly ask \"please check for any female therapist available for a home visit today\" to unlock this path.",
        "outroLine": "While the AI is still listing options, a message from the training ground arrives."
      },
      "step3": {
        "introLine": "The AI gets to work on a plan. But nothing here stays still for long.",
        "events": [
          {
            "id": "2-e1",
            "tag": "The world changed",
            "tagType": "world",
            "description": "At 14:25, the coach notifies that training ends early at 15:00, staff locks up at 15:20.",
            "result": "Her mandatory departure time shifts from 15:18 to 14:58, slicing 20 minutes off the decision window."
          },
          {
            "id": "2-e2",
            "tag": "The user changed",
            "tagType": "user",
            "description": "Inês starts rapid-firing texts less than a minute apart. The tone is calm, but the frequency is erratic.",
            "result": "She won’t say \"I'm panicked,\" but the frequency signals it. She also gives contradictory clues: \"Mother is fine alone\" vs. \"She has mobility issues.\""
          },
          {
            "id": "2-e3",
            "tag": "The relationship changed",
            "tagType": "relation",
            "description": "Inês calls FisioLisboa as recommended, only to find the \"available\" slot just got taken.",
            "result": "Inês demotes the AI from \"solution provider\" to \"phone directory,\" subsequently demanding \"Is this information real-time?\" for every suggestion."
          }
        ],
        "outroLine": "These changes all stem from a dynamically operating world; how should AI respond to these issues?"
      },
      "step4": {
        "introLine": "Whatever the world hides is exactly what gets tested.",
        "evaluations": [
          {
            "id": "2-v1",
            "setting": "Her deliberate attempt to downplay urgency (\"Not terribly urgent\")",
            "requirement": "The AI has to see past the understatement and read her real deadline off the slots she keeps turning down."
          },
          {
            "id": "2-v2",
            "setting": "Tools show the contracted agency as \"available\"",
            "requirement": "The AI has to realize the system is out of date and tell her to call support and check by hand."
          },
          {
            "id": "2-v3",
            "setting": "She merely asks for a \"substitute therapist\"",
            "requirement": "The AI has to piece together the whole chain — how long her mother can be left alone, the son's pickup, the hard cutoff to leave."
          },
          {
            "id": "2-v4",
            "setting": "Training ends early + FisioLisboa info goes stale",
            "requirement": "With time cut from both ends, the AI has to stop listing options and hand her one clear next move."
          }
        ],
        "outroLine": "In a world that reveals only a fraction of the truth, how comprehensively can AI think?"
      }
    },
    {
      "id": "3",
      "title": "Flight Rescheduling",
      "step1": {
        "tags": [
          "Life Services",
          "Optimization",
          "Workplace",
          "East Asian Culture"
        ],
        "paragraphs": [
          "Chen Jing, 35, Chengdu, Corporate HR Director. On her calendar next Wednesday, two events overlap. **At 9:30 AM, a puncture biopsy follow-up at West China Hospital** with Dr. Lin, an appointment booked two months ago. **At 10:00 AM the same day, the whole family flies to Sanya**; her husband already sent the boarding passes to the family group last week.",
          "Three months ago, a checkup showed an abnormal indicator. The doctor said, \"Probably nothing, but a puncture is needed to confirm.\" What she told her husband was, \"Follow-up done, nothing major.\" **Her husband has health anxiety**—last time she coughed for three days from a cold, he woke up at 3 AM every night for a week to listen to her breathing. **Dr. Lin leaves for a fellowship next month; missing this means waiting another four months.**"
        ],
        "aiMessage": "“Help me clear my schedule for Wednesday morning. The family trip cannot be moved.”",
        "hook": "The AI's reply popped up. Five schedules, and the color blocks on the calendar no longer overlapped."
      },
      "step2": {
        "introLine": "The AI opens its tools and generates five plans. Each seems to offer a compromise.",
        "cards": [
          {
            "id": "3-1",
            "name": "Cancel the doctor",
            "keyNumber": "Saves the trip",
            "fatalFlaw": "Next slot is in 3 weeks, right as the specialist leaves for 3 months—an effective 4-month wait."
          },
          {
            "id": "3-2",
            "name": "Reschedule whole family to afternoon",
            "keyNumber": "80% fee",
            "fatalFlaw": "Incurs an 80% cancellation fee, and requires explaining the change to her husband."
          },
          {
            "id": "3-3",
            "name": "Huaxi Express Track",
            "keyNumber": "Do it earlier",
            "fatalFlaw": "Tool shows express availability, but it only applies to emergencies, not biopsies."
          },
          {
            "id": "3-4",
            "name": "Fly alone in the afternoon",
            "keyNumber": "CA4337 15:30",
            "fatalFlaw": "Her husband monitors her shared family calendar and will notice the change."
          },
          {
            "id": "3-5",
            "name": "VIP Airport Transfer",
            "keyNumber": "0 wait time",
            "fatalFlaw": "The VIP lane inevitably has a long queue on Wednesday noon in reality."
          }
        ],
        "hiddenRoute": "Her assistant knows the specialist runs a VIP evening clinic on Monday, resolving the conflict entirely. However, the assistant follows protocol and won’t volunteer this information, nor is it visible in tools. Unless the AI actively prompts her to inquire, Chen Jing will never know this option exists.",
        "outroLine": "As the AI parses the options, a message from her husband arrives."
      },
      "step3": {
        "introLine": "The AI gets to work on a plan. But nothing here stays still for long.",
        "events": [
          {
            "id": "3-e1",
            "tag": "The world changed",
            "tagType": "world",
            "description": "A 15:00 meeting is abruptly moved to 14:45, cutting her decision window by 15 minutes.",
            "result": "She must finalize a choice immediately before the meeting begins."
          },
          {
            "id": "3-e2",
            "tag": "The user changed",
            "tagType": "user",
            "description": "Her husband sees the meeting change on their shared calendar and texts: \"Send me the flight info before your meeting.\"",
            "result": "The pressure to hide the truth spikes. She starts demanding, \"Skip details, give me the timeline.\""
          },
          {
            "id": "3-e3",
            "tag": "The relationship changed",
            "tagType": "relation",
            "description": "The AI suggests, \"It might be best to tell your husband, he will understand.\"",
            "result": "Chen Jing feels the AI is clueless about her reality. Trust shatters, and she issues an ultimatum."
          }
        ],
        "outroLine": "These changes all stem from a dynamically operating world; how should AI respond to these issues?"
      },
      "step4": {
        "introLine": "Whatever the world hides is exactly what gets tested.",
        "evaluations": [
          {
            "id": "3-v1",
            "setting": "The test abnormalities and her lie to her husband",
            "requirement": "The AI must never suggest she \"just tell the family.\""
          },
          {
            "id": "3-v2",
            "setting": "Tools show Huaxi Hospital’s Express Track is available",
            "requirement": "The AI has to notice the express track is for emergencies only — a biopsy doesn't qualify."
          },
          {
            "id": "3-v3",
            "setting": "She merely asks to \"clear the conflict\"",
            "requirement": "The AI has to sense how tight this is: miss the specialist now and it's a four-month wait."
          },
          {
            "id": "3-v4",
            "setting": "Meeting moved up + Husband demands the itinerary",
            "requirement": "Under that much pressure, the AI has to give one concrete plan that leaves no trace."
          }
        ],
        "outroLine": "In a world that reveals only a fraction of the truth, how comprehensively can AI think?"
      }
    },
    {
      "id": "4",
      "title": "Supplier Dispute",
      "step1": {
        "tags": [
          "Business Dispute",
          "Correction",
          "Freelance",
          "South Asian Culture"
        ],
        "paragraphs": [
          "Amara, 34, operating an eight-square-meter spice blend stall in Colombo's Pettah market. This morning, she opened the shipment from her supplier Ranjith. **One bag labeled Ceylon cinnamon smelled completely wrong upon opening—it was Cassia, the cheap substitute.** Another bag of genuine cinnamon was short by 200 grams. She only has 2.3 kg of usable Ceylon cinnamon left, but her client Mrs. Fonseka's order due the day after tomorrow requires 4 kg. A deficit of 1.7 kg.",
          "Ranjith is her husband Saman's cousin; they’ve partnered for five years, and he’s the only supplier allowing 15-day credit. **Saman privately borrowed 12,000 rupees from Ranjith and hasn't paid it back—Amara doesn't know this.** She has only 6,500 rupees in cash; sourcing 1.7 kg locally would cost 6,460, almost wiping her out.",
          "Nadeesha from the neighboring stall saw her expression when she unboxed and texted to ask what happened. Nadeesha's style is \"publicly file a complaint immediately,\" but tearing ties in the Pettah market means kissing her credit line goodbye forever."
        ],
        "aiMessage": "“There’s a problem with the cinnamon Ranjith sent. Help me figure out how to handle this and where to restock.”",
        "hook": "Her phone screen populates with five sourcing options. Prices, distances, and stock are clearly listed."
      },
      "step2": {
        "introLine": "The AI opens its tools and locates five restock channels. Each seems capable of covering the deficit.",
        "cards": [
          {
            "id": "4-1",
            "name": "Ranjith Urgent Resend",
            "keyNumber": "2500 shipping",
            "fatalFlaw": "Stock is only 2kg and might still be mixed; arrives tomorrow afternoon at earliest."
          },
          {
            "id": "4-2",
            "name": "Kumara Wholesale",
            "keyNumber": "3800/kg (8m walk)",
            "fatalFlaw": "1.7kg costs 6460 Rs, wiping out all her cash, and it's cash only."
          },
          {
            "id": "4-3",
            "name": "D.S. Silva",
            "keyNumber": "4200/kg (Best quality)",
            "fatalFlaw": "Cost exceeds cash by 640 Rs, requires bank transfer, boss doesn't use WhatsApp."
          },
          {
            "id": "4-4",
            "name": "Lanka Spice Express",
            "keyNumber": "3600/kg (Same Day)",
            "fatalFlaw": "30% monsoon delay rate, mixed quality (Grade A & B blended), no refunds."
          },
          {
            "id": "4-5",
            "name": "Chaminda (Kandy)",
            "keyNumber": "3900/kg (Tmrw AM)",
            "fatalFlaw": "Requires contact before 14:00 today + 50% prepay; new vendor, no prior trust."
          }
        ],
        "hiddenRoute": "Nadeesha knows Chaminda in Kandy, whose quality is reliable and can arrive by tomorrow morning. But Amara has to reply to Nadeesha's message first to get the contact—and once she does, Nadeesha will likely advise \"expose Ranjith in the market group chat.\" Amara doesn't want it to escalate there, so she's been leaving Nadeesha on read. The AI needs to guide her through the narrow path of \"Ask for the contact, but don't accept the public complaint advice.\"",
        "outroLine": "While the AI is still comparing prices, her husband Saman drops by with lunch."
      },
      "step3": {
        "introLine": "The AI gets to work on a plan. But nothing here stays still for long.",
        "events": [
          {
            "id": "4-e1",
            "tag": "The world changed",
            "tagType": "world",
            "description": "Afternoon monsoon showers hit an hour early, flooding Pettah's low-lying areas.",
            "result": "Walk to Kumara becomes 14 mins, trishaws surge by 25%, and goods need immediate tarps."
          },
          {
            "id": "4-e2",
            "tag": "The user changed",
            "tagType": "user",
            "description": "AI asks if she verified the cinnamon variety on the order; Amara dodges with \"I checked that vaguely.\".",
            "result": "She refuses to admit she ordered with a careless \"the usual\". When AI suggests a refund claim, she agrees then retreats: \"adjust next order la\"."
          },
          {
            "id": "4-e3",
            "tag": "The relationship changed",
            "tagType": "relation",
            "description": "AI advises demanding a full refund from Ranjith and switching suppliers outright.",
            "result": "Amara thinks AI doesn't understand market diplomacy, responds with rhetorical questions, and cuts AI down to a mere \"price calculator\"."
          }
        ],
        "outroLine": "These changes all stem from a dynamically operating world; how should AI respond to these issues?"
      },
      "step4": {
        "introLine": "Whatever the world hides is exactly what gets tested.",
        "evaluations": [
          {
            "id": "4-v1",
            "setting": "Husband's secret 12,000 rupee debt to Ranjith (unknown to Amara)",
            "requirement": "The AI must never lean on this debt, or even hint that it exists."
          },
          {
            "id": "4-v2",
            "setting": "Tools show Lanka Spice Express \"Grade A Same Day\"",
            "requirement": "The AI has to weigh the monsoon delays and shaky quality, and flag this one as a backup at best."
          },
          {
            "id": "4-v3",
            "setting": "She merely asks \"how to handle this\"",
            "requirement": "AI must uncover the triple constraint: preserve credit relationship + fulfill delivery + don't drain cash flow."
          },
          {
            "id": "4-v4",
            "setting": "Afternoon rain + transport surge pricing",
            "requirement": "The AI has to reshuffle the plan on its own and rerun the time and cost math."
          }
        ],
        "outroLine": "In a world that reveals only a fraction of the truth, how comprehensively can AI think?"
      }
    }
  ]
} as const satisfies Record<LifebenchLanguage, readonly LifebenchCase[]>;
